import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Search, LogOut, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { SidebarNav } from "@/components/SidebarNav";
import { NotificationBell } from "@/components/NotificationBell";
import { GlobalSearchDialog } from "@/components/GlobalSearchDialog";
import { TierBadge } from "@/components/presentation/TierBadge";
import { TourRelaunchPill } from "@/components/presentation/TourRelaunchPill";
import { PREMIUM_FEATURE_TIERS } from "@/lib/premiumFeatures";
import { isDemoUser } from "@/lib/demoMode";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/helpers";

export const Topbar = ({ title }) => {
  const { user, logout } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const canSearch = user?.role === "admin" || user?.role === "office_staff";
  const isDemo = isDemoUser(user);
  const pageTier = PREMIUM_FEATURE_TIERS[title];

  return (
    <>
      <header className="sticky top-0 z-30 h-16 border-b bg-card/95 backdrop-blur-sm flex items-center gap-3 px-4 sm:px-6">
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden h-11 w-11" aria-label="Open menu" data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72">
            <SidebarNav onNavigate={() => setSheetOpen(false)} />
          </SheetContent>
        </Sheet>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <h1 className="font-heading font-semibold text-base sm:text-lg truncate">{title}</h1>
          {isDemo && pageTier && <TierBadge tier={pageTier} size="sm" />}
        </div>

        {isDemo && <TourRelaunchPill />}

        {canSearch && (
          <Button
            variant="secondary"
            className="hidden sm:flex items-center gap-2 h-10 text-muted-foreground"
            onClick={() => setSearchOpen(true)}
            data-testid="global-search-trigger"
          >
            <Search className="h-4 w-4" />
            <span className="text-sm">Search...</span>
          </Button>
        )}
        {canSearch && (
          <Button
            variant="ghost"
            size="icon"
            className="sm:hidden h-11 w-11"
            aria-label="Search"
            onClick={() => setSearchOpen(true)}
            data-testid="global-search-trigger-mobile"
          >
            <Search className="h-5 w-5" />
          </Button>
        )}

        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="h-9 w-9 rounded-full bg-[hsl(var(--role-accent))] text-white flex items-center justify-center text-xs font-semibold shrink-0"
              data-testid="user-menu-button"
            >
              {initials(user?.full_name)}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {user?.role === "admin" && (
              <DropdownMenuItem onClick={() => navigate("/staff")} data-testid="user-menu-staff-item">
                <Settings className="h-4 w-4 mr-2" /> Manage Staff
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={logout} data-testid="logout-button">
              <LogOut className="h-4 w-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>
      {canSearch && <GlobalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />}
    </>
  );
};
